import React from "react";
import { Loader, Button } from "@mantine/core";
import { withRouter } from "../utilities/withRouter";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
	getFavoriteDocs,
	getRecentDocsAndMatters,
} from "../actions/documentActions";
import { getMatterLink, iconMap } from "../utilities/utilities";
import { humanReadableTimeDifferenece } from "../utilities/dateUtils";
import DropDownFriendlyList from "./DropDownFriendlyList";
import { IconChevronDown } from "@tabler/icons-react";

const COLUMN_WIDTH = 300;
const MAX_RECENT_DOCS_TO_SHOW = 12;

const bottomPadding = (el) =>
	parseFloat(getComputedStyle(el).paddingBottom) || 0;

// How many unpinned items in each column fit fully above the menu's bottom edge
const countItemsThatFit = (menuEl, columnsEl) => {
	const menuBottom = menuEl.getBoundingClientRect().bottom;
	return Array.from(columnsEl.children).map((columnEl) => {
		const itemEls = Array.from(
			columnEl.querySelectorAll(".DropdownListItem")
		);
		if (!itemEls.length) return null;
		const listEl = itemEls[0].parentElement;
		const limit =
			menuBottom - bottomPadding(columnEl) - bottomPadding(listEl);
		const pinnedHeight = itemEls
			.filter((el) => el.dataset.pinned)
			.reduce((sum, el) => sum + el.getBoundingClientRect().height, 0);
		const unpinnedEls = itemEls.filter((el) => !el.dataset.pinned);
		const fitIndex = unpinnedEls.findIndex(
			(el) => el.getBoundingClientRect().bottom + pinnedHeight > limit
		);
		return fitIndex === -1 ? null : fitIndex;
	});
};

const styles = {
	menu: {
		display: "flex",
		position: "absolute",
		top: "100%",
		left: "0",
		zIndex: "1000",
		float: "left",
		padding: "0px 0px",
		margin: "2px 0 0",
		fontSize: "14px",
		textAlign: "left",
		listStyle: "none",
		backgroundColor: "#fff",
		WebkitBackgroundClip: "padding-box",
		backgroundClip: "padding-box",
		border: "1px solid rgba(0,0,0,.15)",
		borderRadius: "4px",
		WebkitBoxShadow: "0 6px 12px rgba(0,0,0,.175)",
		boxShadow: "0 6px 12px rgba(0,0,0,.175)",
		lineHeight: "36px",
	},
	listItem: {
		display: "block",
		padding: "7px 5px",
		clear: "both",
		fontWeight: "400",
		lineHeight: "1.42857143",
		width: COLUMN_WIDTH,
		maxWidth: COLUMN_WIDTH,
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	listItemLink: {
		color: "var(--lxw-main-text)",
		textDecoration: "none",
	},
	listItemSubtext: {
		color: "grey",
		marginLeft: 22,
		fontSize: ".83em",
	},
};

const listLengthsKey = (dropdownData) =>
	dropdownData
		? dropdownData.columns.map((column) => column.list.length).join()
		: "";

class MultiColumnDropdown extends React.Component {
	constructor(props, context) {
		super(props, context);
		this.openDropdown = this.openDropdown.bind(this);
		this.closeDropdown = this.closeDropdown.bind(this);
		this.container = React.createRef();
		this.menu = React.createRef();
		this.columns = React.createRef();

		this.state = {
			isDropdownOpen: false,
			dropdownData: this.props.dropdownData,
			loading: false,
			visibleCounts: null,
		};
	}
	componentDidMount() {
		document.addEventListener("mousedown", this.handleClickOutside);
		if (this.props.trimToFit) {
			window.addEventListener("resize", this.resetVisibleCounts);
		}
	}
	componentDidUpdate(prevProps) {
		if (!this.props.trimToFit) return;
		if (
			listLengthsKey(prevProps.dropdownData) !==
				listLengthsKey(this.props.dropdownData) ||
			(prevProps.loading && !this.props.loading)
		) {
			this.resetVisibleCounts();
		} else if (
			this.state.visibleCounts === null &&
			this.menu.current &&
			this.columns.current
		) {
			this.setState({
				visibleCounts: countItemsThatFit(
					this.menu.current,
					this.columns.current
				),
			});
		}
	}
	componentWillUnmount() {
		document.removeEventListener("mousedown", this.handleClickOutside);
		window.removeEventListener("resize", this.resetVisibleCounts);
	}
	resetVisibleCounts = () => {
		this.setState({ visibleCounts: null });
	};
	openDropdown = () => {
		this.setState(
			{ loading: true, isDropdownOpen: true, visibleCounts: null },
			this.props.loadData
		);
		if (this.props.onOpen && !this.state.isDropdownOpen) {
			this.props.onOpen();
		}
	};
	closeDropdown = () => {
		this.setState(() => ({
			isDropdownOpen: false,
		}));
	};
	handleClickOutside = (event) => {
		if (
			this.container.current &&
			!this.container.current.contains(event.target)
		) {
			this.setState(() => ({
				isDropdownOpen: false,
			}));
		}
	};
	render() {
		return (
			<div
				className={
					"DropDownButton" +
					(this.props.className ? " " + this.props.className : "")
				}
				ref={this.container}
				style={{ position: "relative" }}
			>
				<Button
					variant={this.props.buttonVariant || "default"}
					onClick={
						this.state.isDropdownOpen
							? this.closeDropdown
							: this.openDropdown
					}
					leftSection={this.props.buttonIcon || null}
					rightSection={<IconChevronDown size={14} />}
				>
					{this.props.dropdownData.title}
				</Button>
				{this.state.isDropdownOpen && (
					<div
						ref={this.menu}
						style={{
							...styles.menu,
							width: this.props.customColumnWidth
								? this.props.customColumnWidth
								: "auto",
							maxHeight: "calc(100vh - 90px)",
							...(this.props.trimToFit
								? { overflow: "hidden" }
								: { overflowY: "auto", overflowX: "hidden" }),
						}}
					>
						{this.props.dropdownData && !this.props.loading ? (
							<div ref={this.columns} style={{ display: "flex" }}>
								{this.props.dropdownData.columns.map(
									(column, columnIndex) => (
										<DropDownFriendlyList
											key={columnIndex}
											items={column.list}
											maxItems={
												this.state.visibleCounts
													? this.state.visibleCounts[
															columnIndex
														]
													: null
											}
											title={column.title}
											emptyStateText={
												column.noneText || "(none)"
											}
											width={
												this.props.customColumnWidth ||
												COLUMN_WIDTH
											}
											renderItem={({
												item,
												key,
												onClick: onItemClick,
											}) => {
												return this.props.dropdownData.listItemComponentFn(
													{
														item,
														key,
														onClick: () => {
															this.closeDropdown();
															if (onItemClick)
																onItemClick();
														},
													}
												);
											}}
										/>
									)
								)}
							</div>
						) : (
							<div
								style={{
									paddingTop: 20,
									width: "180px",
									textAlign: "center",
								}}
							>
								<Loader />
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

const FavoriteDocumentsItem = ({ item, onClick }) => {
	const { iconComponent } = iconMap({
		original: {
			...item,
			type: item.type ? item.type : "File",
		},
	});
	return (
		<Link
			title={item.name ? item.name : item.filename}
			to={item.url}
			style={styles.listItemLink}
			onClick={onClick}
		>
			<div style={styles.listItem}>
				{!item.type ? null : iconComponent ? iconComponent : null}
				{item.name ? item.name : item.filename}
				{item.filename && item.sortDate ? (
					<div style={styles.listItemSubtext}>
						{item.docType === "document"
							? `Uploaded ${humanReadableTimeDifferenece(item.sortDate)}`
							: `Version saved ${humanReadableTimeDifferenece(item.sortDate)}`}
					</div>
				) : null}
				{item.subtext ? (
					<div style={styles.listItemSubtext}>{item.subtext}</div>
				) : null}
			</div>
		</Link>
	);
};

/*
Multiple Column Dropdown Button React Component
Example data to pass to component:
	{
		dropdownData: {
			title: "Favorite",
			listItemComponentFn:({item,onClick})=><SomeListItemComponent />,

			columns: [
				{
					title: "Documents",
					list: [
						{
							type: "File",
							filename: "WordDoc DOC.docx",
							url: "matters",
						},

						{
							type: "File",
							filename: "worddoc.docx",
							url: "matters",
						},
						{
							type: "File",
							filename: "Spreadsheet.xlsx",
							url: "matters",
						},
						{
							filename: "See All...",
							url:
								"/search?runAfterNavigating=1&q=&m=&type=0&from=&to=&docType=&docStatus=&docTags=&users=&lock=&userOp=&fav=1",
						},
					],
				},
				{
					title: "Folders",
					list: [
						{
							type: "Folder",
							filename: "Folder01",
							url: "matters",
						},
						{
							type: "Folder",
							filename: "Folder02",
							url: "matters",
						},
						{
							type: "Folder",
							filename: "Folder03",
							url: "matters",
						},
					],
				},
			],
		},
		loading: state.document.dropdownDataLoading,
	}
*/

const RecentItemsDropdown = connect(
	(state, ownProps) => {
		var recentDocs = [];
		state.document.recentDocs
			.slice(0, MAX_RECENT_DOCS_TO_SHOW)
			.forEach(function (item) {
				recentDocs.push({
					type: "File",
					filename: item.filename,
					url: "/docs/" + item.id,
					docType: item.type,
					sortDate: item.sortDate,
				});
			});

		recentDocs.push({
			filename: "See All...",
			pinned: true,
			url: `/search/${Math.floor(
				Math.random() * 10000
			)}?runAfterNavigating=1&q=&m=&type=0&from=&to=&docType=&docStatus=&docTags=&users=${
				state.user.userID
			}&lock=&userOp=&fav=0&order=0`,
		});

		const recentMatters = state.document.recentMatters.map((item) => ({
			type: "Matter",
			name: item.name,
			subtext: item.clientName,
			url: getMatterLink(item.id),
		}));

		const recentClients = state.document.recentClients.map((item) => ({
			type: "Client",
			name: item.name,
			subtext: item.tags.join(", "),
			url: `/clients/${item.id}`,
		}));

		return {
			dropdownData: {
				title: ownProps.title || "Recent",
				columns: [
					{
						title: "Clients",
						list: recentClients,
						noneText: "No Recent Clients",
					},
					{
						title: "Matters",
						list: recentMatters,
						noneText: "No Recent Matters",
					},
					{
						title: "Documents",
						list: recentDocs,
						noneText: "No Recent Documents",
					},
				],
				listItemComponentFn: ({ ...listItemProps }) => {
					return <FavoriteDocumentsItem {...listItemProps} />;
				},
			},
			loading: state.document.recentDocsLoading,
			trimToFit: true,
		};
	},
	{
		loadData: getRecentDocsAndMatters,
	}
)(withRouter(MultiColumnDropdown));

const FavoriteDocsDropdown = connect(
	(state) => {
		var favoriteDocs = [];
		var favoriteFolders = [];
		state.document.favoriteDocs.forEach(function (item) {
			favoriteDocs.push({
				type: "File",
				filename: item.filename,
				url: "/docs/" + item.id,
			});
		});
		state.document.favoriteFolders.forEach(function (item) {
			favoriteFolders.push({
				type: "Folder",
				filename: item.filename,
				url: item.url,
			});
		});

		if (favoriteDocs.length) {
			favoriteDocs.push({
				filename: "See All...",
				pinned: true,
				url: "/search?runAfterNavigating=1&q=&m=&type=0&from=&to=&docType=&docStatus=&docTags=&users=&lock=&userOp=&fav=1",
			});
		}
		return {
			dropdownData: {
				title: "Favorite",
				columns: [
					{
						title: "Documents",
						list: favoriteDocs,
						noneText: "No Favorite Documents",
					},
					{
						title: "Folders",
						list: favoriteFolders,
						noneText: "No Favorite Folders",
					},
				],
				listItemComponentFn: ({ ...listItemProps }) => {
					return <FavoriteDocumentsItem {...listItemProps} />;
				},
			},
			loading: state.document.favoriteDocsLoading,
		};
	},
	{
		loadData: getFavoriteDocs,
	}
)(withRouter(MultiColumnDropdown));

export { FavoriteDocsDropdown, MultiColumnDropdown, RecentItemsDropdown };
